// The complete reference for the fields can be located at the marine-traffic-api documentation
// https://intransit.marinetraffic.com/#operation/getShipmentById

import { ProgressBarContainerEvent } from "../components/VoyageDetailsComponent";
import { EventTypeCode } from "../util/enums";

interface PortId {
  portId: number;
}

interface ShipId {
  shipId: number | null;
  vesselName?: string | null;
}

interface EventId {
  eventId: number;
}

export interface Event {
  carrierVoyageNumber?: string | null;
  eventCategory: string;
  eventDatetime?: string;
  eventId: number;
  eventStatus: string;
  eventTypeCode: EventTypeCode;
  eventTypeDescription: string;
  location: PortId;
  vessel: ShipId;
}

export enum EventStatus {
  DONE = "done",
  IN_PROGRESS = "in progress",
  WAITING = "waiting",
  CANCELLED = "cancelled",
  DELIVERED = "delivered",
  LOADED = "loaded",
  INSPECT = "inspect",
  TRANSSHIPPED = "transshipped",
}

export enum EventsIcons {
  HOME = "home",
  PLANE = "plane",
  SHIP = "ship",
  TRUCK = "truck",
}

export interface Location {
  UNLocationCode: string;
  lat: string;
  lon: string;
  localTimeOffset: number;
  locationName: string;
  portId: number;
  locationCountry?: string;
}

export interface Route {
  carrierVoyageNumber: string;
  arrival: EventId;
  departure: EventId;
}

export interface TransportLeg {
  anchorageActualArrivalUtc?: string;
  carrierVoyageNumber?: string;
  portActualArrivalUtc?: string;
  portActualDepartureUtc?: string;
  portPredictiveArrivalUtc?: string;
  portPredictiveDepartureUtc?: string;
  terminalActualArrivalUtc?: string;
  transportLegId: string;
  arrivalLocation: PortId;
  departureLocation: PortId;
}

export interface Vessel {
  shipId: number;
  vesselIMONumber: number;
  vesselMMSINumber: number;
  vesselType: string;
  vesselName: string;
}

export interface TransportationPlan {
  events: Event[];
  locations: Location[];
  routes: Route[];
  transportLegs: TransportLeg[];
  vessels: Vessel[];
}

export interface Subscription {
  subscription: {
    scac: string;
    shipmentId: string;
    transportDocumentId: string;
    transportDocumentType: string;
  };
}

export interface StatusProgress {
  events: ProgressBarContainerEvent[];
  activeStep: number;
}

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface ShipmentPosition {
  positionsConclude: Coordinates[];
  positionsInProgress: Coordinates[];
}
export interface ShipmentLocation {
  departure: Location | undefined;
  arrival: Location | undefined;
}

export type LastContainerPosition = {
  position?: Coordinates;
  vesselName?: string;
  lastUpdate?: string | Date;
};
export interface ContainerStatus {
  originPortPredictiveDepartureUtc?: string;
  originPortActualDepartureUtc?: string;
  finalPortActualArrivalUtc?: string;
  finalPortAnchorageActualArrivalUtc?: string;
  finalPortCarrierEstimatedArrivalUtc?: string;
  originPortId?: number;
  finalPortId: number;
  finalPortLocalTimeOffset: number;
  finalPortName?: string;
  originPortName?: string;
  finalPortPredictiveArrivalUtc?: string;
  finalPortPredictiveArrivalUtcLast?: string;
  finalPortTerminalActualArrivalName?: string;
  finalPortTerminalActualArrivalUtc?: string;
  finalPortUNLocationCode?: string;
  nextPortDistanceToGo?: number;
  nextPortId: number;
  nextPortName?: string;
  nextPortPredictiveArrivalUtc?: string;
  nextPortUNLocationCode?: string;
  scac: string;
  shipmentId: string;
  transportDocumentId: string;
  transportDocumentType: string;
  transportationPlan: TransportationPlan;
  vesselIMONumber: number;
  vesselName: string;
  vesselType: string;
  vesselShipId: number;
  statusProgress?: StatusProgress;
  position?: ShipmentPosition;
  allPositions?: Coordinates[];
  shipmentLocation?: ShipmentLocation;
  eventsContainer?: ProgressBarContainerEvent[];
  plannedEta?: string;
  lastPosition?: LastContainerPosition;
  carrier?: string;
}

/**
 * @description A representation of the object that is received from marine traffic when it makes the request to retrieve the vessel's position
 *
 * @property MMSI: string | null:
 * Maritime Mobile Service Identity - a nine-digit number sent in digital form over a radio frequency that identifies the vessel's transmitter station
 * @property LAT: string | null:
 * Latitude - a geographic coordinate that specifies the north-south position of the vessel on the Earth's surface
 * @property LON: string | null:
 * Longitude - a geographic coordinate that specifies the east-west position of the vessel on the Earth's surface
 * @property SPEED: string | null:
 * The speed (in knots x10) that the subject vessel is reporting according to AIS transmissions
 * @property HEADING: string | null:
 * The heading (in degrees) that the subject vessel is reporting according to AIS transmissions
 * @property COURSE: string | null:
 * The course (in degrees) that the subject vessel is reporting according to AIS transmissions
 * @property STATUS: string | null:
 * The AIS Navigational Status of the subject vessel as input by the vessel's crew. There might be discrepancies with the vessel's detail page when vessel speed is near zero (0) knots
 * @property TIMESTAMP: string | null:
 * The date and time (in UTC) that the subject vessel's position or event was recorded by MarineTraffic
 * @property SHIPNAME: string | null:
 * The Shipname of the subject vessel
 * @property SHIPTYPE: string | null:
 * The Shiptype of the subject vessel according to AIS transmissions
 * @property TYPE_NAME: string | null:
 * The Type of the subject vessel
 * @property AIS_TYPE_SUMMARY: string | null:
 * Further explanation of the SHIPTYPE ID
 * @property IMO: string | null:
 * International Maritime Organisation number - a seven-digit number that uniquely identifies vessels
 * @property CALLSIGN: string | null:
 * A uniquely designated identifier for the vessel's transmitter station
 * @property FLAG: string | null:
 * The flag of the subject vessel according to AIS transmissions
 * @property PORT_ID: string | null:
 * A uniquely assigned ID by MarineTraffic for the Current Port
 * @property PORT_UNLOCODE: string | null:
 * A uniquely assigned ID by United Nations for the Current Port
 * @property CURRENT_PORT: string | null:
 * The name of the Port the subject vessel is currently in (NULL if the vessel is underway)
 * @property LAST_PORT_ID: string | null:
 * A uniquely assigned ID by MarineTraffic for the Last Port
 * @property LAST_PORT_UNLOCODE: string | null:
 * A uniquely assigned ID by United Nations for the Last Port
 * @property LAST_PORT: string | null:
 * The Name of the Last Port the vessel has visited
 * @property LAST_PORT_TIME: string | null:
 * The Date and Time (in UTC) that the subject vessel departed from the Last Port
 * @property DESTINATION: string | null:
 * The Destination of the subject vessel according to the AIS transmissions
 * @property ETA: string | null:
 * The Estimated Time of Arrival to Destination of the subject vessel according to the AIS transmissions
 * @property ETA_CALC: string | null:
 * The Estimated Time of Arrival to Destination of the subject vessel according to the MarineTraffic calculations
 * @property LENGTH: string | null:
 * The overall Length (in metres) of the subject vessel
 * @property WIDTH: string | null:
 * The Breadth (in metres) of the subject vessel
 * @property DRAUGHT: string | null:
 * The Draught (in metres x10) of the subject vessel according to the AIS transmissions
 * @property GRT: string | null:
 * Gross Tonnage - unitless measure that calculates the moulded volume of all enclosed spaces of a ship
 * @property NEXT_PORT_ID: string | null:
 * A uniquely assigned ID by MarineTraffic for the Next Port
 * @property NEXT_PORT_UNLOCODE: string | null:
 * A uniquely assigned ID by United Nations for the Next Port
 * @property NEXT_PORT_NAME: string | null:
 * The Name of the Next Port as derived by MarineTraffic based on the subject vessel's reported Destination
 * @property NEXT_PORT_COUNTRY: string | null:
 * The Country that the Next Port is located at
 * @property DWT: string | null:
 * Deadweight - a measure (in metric tons) of how much weight a vessel can safely carry (excluding the vessel's own weight)
 * @property YEAR_BUILT: string | null:
 * The year that the subject vessel was built
 * @property DSRC: string | null:
 * Data Source - Defines whether the transmitted AIS data was received by a Terrestrial or a Satellite AIS Station
 *
 * @example const containerPosition: ContainerPosition = { MMSI,  "563052700", LAT,  "7.385537", LON,  "-142.515900", SPEED,  "199", HEADING,  "219", COURSE,  "221", STATUS,  "0", TIMESTAMP,  "2022-03-18T17:46:47", SHIPNAME,  "CAP JERVIS", SHIPTYPE,  "74", TYPE_NAME,  "Container Ship", AIS_TYPE_SUMMARY,  "Cargo", IMO,  "9484572", CALLSIGN,  "9V6246", FLAG,  "SG", PORT_ID,  null, PORT_UNLOCODE,  null, CURRENT_PORT,  null, LAST_PORT_ID,  "2727", LAST_PORT_UNLOCODE,  "USLGB", LAST_PORT,  "LONG BEACH", LAST_PORT_TIME,  "2022-03-14T00:32:00", DESTINATION,  "USLGB>NZAKL", ETA,  "2022-03-26T01:00:00", ETA_CALC,  "2022-03-26T04:13:00", LENGTH,  "264.32", WIDTH,  "32.29", DRAUGHT,  "122", GRT,  "47877", NEXT_PORT_ID,  "769", NEXT_PORT_UNLOCODE,  "NZAKL", NEXT_PORT_NAME,  "AUCKLAND", NEXT_PORT_COUNTRY,  "NZ", DWT,  "59266", YEAR_BUILT,  "2010", DSRC,  "SAT"}
 */
export type ContainerPosition = {
  /**
   * Maritime Mobile Service Identity - a nine-digit number sent in digital form over a radio frequency that identifies the vessel's transmitter station
   */
  MMSI: string | null;
  /**
   * Latitude - a geographic coordinate that specifies the north-south position of the vessel on the Earth's surface
   */
  LAT: string | null;
  /**
   * Longitude - a geographic coordinate that specifies the east-west position of the vessel on the Earth's surface
   */
  LON: string | null;
  /**
   * The speed (in knots x10) that the subject vessel is reporting according to AIS transmissions
   */
  SPEED: string | null;
  /**
   * The heading (in degrees) that the subject vessel is reporting according to AIS transmissions
   */
  HEADING: string | null;
  /**
   * The course (in degrees) that the subject vessel is reporting according to AIS transmissions
   */
  COURSE: string | null;
  /**
   * The AIS Navigational Status of the subject vessel as input by the vessel's crew. There might be discrepancies with the vessel's detail page when vessel speed is near zero (0) knots
   */
  STATUS: string | null;
  /**
   * The date and time (in UTC) that the subject vessel's position or event was recorded by MarineTraffic
   */
  TIMESTAMP: string | null;
  /**
   * The Shipname of the subject vessel
   */
  SHIPNAME: string | null;
  /**
   * The Shiptype of the subject vessel according to AIS transmissions
   */
  SHIPTYPE: string | null;
  /**
   * The Type of the subject vessel
   */
  TYPE_NAME: string | null;
  /**
   * Further explanation of the SHIPTYPE ID
   */
  AIS_TYPE_SUMMARY: string | null;
  /**
   * International Maritime Organisation number - a seven-digit number that uniquely identifies vessels
   */
  IMO: string | null;
  /**
   * A uniquely designated identifier for the vessel's transmitter station
   */
  CALLSIGN: string | null;
  /**
   * The flag of the subject vessel according to AIS transmissions
   */
  FLAG: string | null;
  /**
   * A uniquely assigned ID by MarineTraffic for the Current Port
   */
  PORT_ID: string | null;
  /**
   * A uniquely assigned ID by United Nations for the Current Port
   */
  PORT_UNLOCODE: string | null;
  /**
   * The name of the Port the subject vessel is currently in (NULL if the vessel is underway)
   */
  CURRENT_PORT: string | null;
  /**
   * A uniquely assigned ID by MarineTraffic for the Last Port
   */
  LAST_PORT_ID: string | null;
  /**
   * A uniquely assigned ID by United Nations for the Last Port
   */
  LAST_PORT_UNLOCODE: string | null;
  /**
   * The Name of the Last Port the vessel has visited
   */
  LAST_PORT: string | null;
  /**
   * The Date and Time (in UTC) that the subject vessel departed from the Last Port
   */
  LAST_PORT_TIME: string | null;
  /**
   * The Destination of the subject vessel according to the AIS transmissions
   */
  DESTINATION: string | null;
  /**
   * The Estimated Time of Arrival to Destination of the subject vessel according to the AIS transmissions
   */
  ETA: string | null;
  /**
   * The Estimated Time of Arrival to Destination of the subject vessel according to the MarineTraffic calculations
   */
  ETA_CALC: string | null;
  /**
   * The overall Length (in metres) of the subject vessel
   */
  LENGTH: string | null;
  /**
   * The Breadth (in metres) of the subject vessel
   */
  WIDTH: string | null;
  /**
   * The Draught (in metres x10) of the subject vessel according to the AIS transmissions
   */
  DRAUGHT: string | null;
  /**
   * Gross Tonnage - unitless measure that calculates the moulded volume of all enclosed spaces of a ship
   */
  GRT: string | null;
  /**
   * A uniquely assigned ID by MarineTraffic for the Next Port
   */
  NEXT_PORT_ID: string | null;
  /**
   * A uniquely assigned ID by United Nations for the Next Port
   */
  NEXT_PORT_UNLOCODE: string | null;
  /**
   * The Name of the Next Port as derived by MarineTraffic based on the subject vessel's reported Destination
   */
  NEXT_PORT_NAME: string | null;
  /**
   * The Country that the Next Port is located at
   */
  NEXT_PORT_COUNTRY: string | null;
  /**
   * Deadweight - a measure (in metric tons) of how much weight a vessel can safely carry (excluding the vessel's own weight)
   */
  DWT: string | null;
  /**
   * The year that the subject vessel was built
   */
  YEAR_BUILT: string | null;
  /**
   * Data Source - Defines whether the transmitted AIS data was received by a Terrestrial or a Satellite AIS Station
   */
  DSRC: string | null;
};
